import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { login, clearError } from '../../store/slices/authSlice';
import { fetchBranding } from '../../store/slices/settingsSlice';
import { switchLanguage, initLoginLanguage } from '../../store/slices/languageSlice';
import { SUPPORTED_LANGUAGES, getLanguageMeta } from '../../i18n/supportedLanguages';
import LoginRenderer from '../../components/LoginRenderer';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { getLocalized } from '../../i18n/getLocalized';

function Login() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth);
  const { branding, brandingLoading } = useSelector((state) => state.settings);
  const currentLanguage = useSelector((s) => s.language.currentLanguage);

  // Stale-while-revalidate: a cached payload paints the login screen instantly
  // (no white flash), but we always ask the server for the current branding so
  // changes the tenant owner just published — hero image, slider slides, layout
  // — show up on this visit instead of the next time the cache happens to be
  // rewritten. `fetchBranding.pending` leaves `brandingLoading` false while a
  // cache exists, so the revalidation is invisible.
  useEffect(() => {
    dispatch(fetchBranding());
  }, [dispatch]);

  // Once branding is fetched, sync the public-side language with tenant default
  // (unless the user has explicitly picked a language in this browser).
  useEffect(() => {
    if (branding?.defaultLanguage) {
      dispatch(initLoginLanguage({ tenantDefaultLanguage: branding.defaultLanguage }));
    }
  }, [dispatch, branding?.defaultLanguage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(login({ email, password }));
    if (login.fulfilled.match(result)) {
      navigate('/dashboard');
    }
  };

  const handleLanguageChange = (lang) => {
    dispatch(switchLanguage(lang));
  };

  const labels = {
    welcomeBack: t('auth.welcomeBack'),
    signInTo: t('auth.signInTo', { company: getLocalized(branding?.companyName, currentLanguage) || '' }),
    emailAddress: t('auth.emailAddress'),
    emailPlaceholder: t('auth.emailPlaceholder'),
    password: t('auth.password'),
    passwordPlaceholder: t('auth.passwordPlaceholder'),
    signingIn: t('auth.signingIn'),
    signIn: t('auth.signIn'),
    poweredBy: t('common.poweredBy'),
    defaultHeadline: t('auth.modernContractManagement'),
    defaultTagline: t('auth.defaultTagline'),
    defaultFeatures: [
      t('auth.feature1'),
      t('auth.feature2'),
      t('auth.feature3'),
      t('auth.feature4'),
    ],
  };

  if (brandingLoading && !branding) {
    return <div style={{ minHeight: '100vh', background: '#ffffff' }} />;
  }

  const supportedLangs = branding?.supportedLanguages?.length
    ? branding.supportedLanguages
    : SUPPORTED_LANGUAGES;

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          top: 14,
          right: 18,
          zIndex: 50,
        }}
      >
        <LanguageSwitcher
          languages={supportedLangs}
          current={currentLanguage || i18n.language}
          onChange={handleLanguageChange}
        />
      </div>
      <LoginRenderer
        branding={branding || {}}
        currentLanguage={currentLanguage || i18n.language}
        defaultLanguage={branding?.defaultLanguage || 'en'}
        email={email}
        password={password}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        onErrorDismiss={() => dispatch(clearError())}
        labels={labels}
      />
    </div>
  );
}

export default Login;
